import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { openConsultationModal } from "./ConsultationModal";
import { ArrowRight } from "lucide-react";
import { LineReveal, Magnetic, Reveal } from "./motion";

function Particles() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current; const ctx = canvas.getContext("2d");
    let w, h, raf; const dots = [];
    const resize = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    resize();
    for (let i = 0; i < 70; i++) dots.push({ x: Math.random() * w, y: Math.random() * h, vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25, r: Math.random() * 1.8 + 0.4 });
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      dots.forEach((d) => {
        d.x += d.vx; d.y += d.vy;
        if (d.x < 0 || d.x > w) d.vx *= -1; if (d.y < 0 || d.y > h) d.vy *= -1;
        ctx.beginPath(); ctx.arc(d.x, d.y, d.r, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255,90,31,0.6)"; ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={ref} className="absolute inset-0 w-full h-full" />;
}

export const Hero = () => {
  const navigate = useNavigate();

  return (
    <section data-testid="hero" className="section-dark relative min-h-0 lg:min-h-[100svh] flex flex-col justify-center overflow-hidden grid-bg-dark pt-28 pb-12 sm:pt-36 sm:pb-24">
      {/* aurora blobs */}
      <div className="blob w-[600px] h-[600px] bg-[#FF5A1F] -top-40 -left-40 animate-aurora" style={{ opacity: 0.4 }} />
      <div className="blob w-[460px] h-[460px] bg-[#c23c00] top-1/3 -right-32 animate-aurora" style={{ opacity: 0.3, animationDelay: "3s" }} />
      <Particles />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
          className="inline-flex items-center gap-2 glass-dark rounded-full px-3.5 py-1.5 mb-5 sm:mb-8 text-xs sm:text-sm font-medium tracking-wide text-neutral-300">
          <span className="w-2 h-2 rounded-full bg-[#FF5A1F] inline-block" /> Enterprise Digital Systems & Solutions
        </motion.div>

        <h1 className="font-display font-bold tracking-tight text-white text-3xl sm:text-6xl lg:text-7xl leading-[1.08] max-w-5xl">
          <LineReveal delay={0.15}>Building <span className="text-gradient">Intelligent</span></LineReveal>
          <LineReveal delay={0.3}>Digital Products</LineReveal>
          <LineReveal delay={0.45}>for Tomorrow</LineReveal>
        </h1>

        <Reveal delay={0.7} className="mt-4 sm:mt-8 max-w-2xl">
          <p className="text-base sm:text-xl text-neutral-400 leading-relaxed">
            Custom software, AI solutions, mobile apps, cloud platforms and digital transformation, engineered for ambitious businesses worldwide.
          </p>
        </Reveal>

        <Reveal delay={0.85} className="mt-6 sm:mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3.5 sm:gap-4 flex-wrap">
          <Magnetic className="w-full sm:w-auto">
            <button data-testid="hero-consult-btn" onClick={() => openConsultationModal()}
              className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full bg-[#FF5A1F] text-white font-semibold hover:bg-[#ff6d38] transition-colors">
              Book Free Consultation <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </Magnetic>

          <Magnetic className="w-full sm:w-auto">
            <button data-testid="hero-services-btn" onClick={() => navigate("/services")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full glass-dark text-white font-semibold hover:border-[#FF5A1F] transition-colors">
              Explore Services
            </button>
          </Magnetic>

          <Magnetic className="w-full sm:w-auto">
            <button data-testid="hero-work-btn" onClick={() => navigate("/portfolio")}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 sm:py-4 rounded-full glass-dark text-white font-semibold hover:border-[#FF5A1F] transition-colors">
              Check Our Work
            </button>
          </Magnetic>
        </Reveal>

      </div>
    </section>
  );
};

export default Hero;
